import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';

import { DevicesContext } from './devices.context';

import {
  DateFilter,
  SelectFilter,
  Table,
  TableCell,
  TableRow,
  WithSpinner,
} from '../../../components';
import {
  checkBoxColor,
  formatLocaleDate,
  generateHeader,
  isFilterActive,
  mapDevicesStatus,
  buildTableFilterBody,
} from '../../../utils';

import { config } from '../../../api/config';
import { actions } from '../../../state';

const PAGE_SIZE = config.DEFAULTS.recordsPerPage;

const DevicesPage = ({
  dispatch,
  devices: { loading, total, devices, header },
  locations: { locations },
}) => {
  const history = useHistory();
  const location = useLocation();
  const {
    page,
    setPage,
    filterBody,
    setFilterBody,
    idFilter,
    setIdFilter,
    statusFilter,
    setStatusFilter,
    deviceNameFilter,
    setDeviceNameFilter,
    locationFilter,
    setLocationFilter,
    errorDescriptionFilter,
    setErrorDescriptionFilter,
    dateTimeLtFilter,
    setDateTimeLtFilter,
    dateTimeGtFilter,
    setDateTimeGtFilter,
    sortOrder,
    setSortOrder,
    clearFilters,
  } = useContext(DevicesContext);

  const pageCount = Math.ceil(total / PAGE_SIZE) || 1;

  const clearDevices = useCallback(() => {
    dispatch(actions.monitoring.clearDevicesAction());
  }, [dispatch]);

  const fetchDevices = useCallback(
    data => {
      dispatch(actions.monitoring.getDevicesAction(data));
    },
    [dispatch]
  );

  const fetchLocations = useCallback(() => {
    dispatch(
      actions.locations.getLocationsAction({
        limit: 1000,
        offset: 0,
        order: [
          {
            by: 'full_address',
            order: 'asc',
          },
        ],
      })
    );
  }, [dispatch]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  useEffect(() => {
    const presetDeviceFilters =
      location.state &&
      typeof location.state === 'object' &&
      location.state.presetDeviceFilters;

    if (!presetDeviceFilters) {
      return;
    }

    setIdFilter(presetDeviceFilters.idFilter || []);
    setDeviceNameFilter([]);
    setLocationFilter([]);
    setStatusFilter([]);
    setErrorDescriptionFilter([]);
    setDateTimeGtFilter();
    setDateTimeLtFilter();
    setSortOrder(presetDeviceFilters.sortOrder || 'asc');
    setFilterBody(presetDeviceFilters.filterBody);
    setPage(1);
    history.replace(`${config.APP_PATHS.monitoring}/devices`);
  }, [
    history,
    location.state,
    setDateTimeGtFilter,
    setDateTimeLtFilter,
    setDeviceNameFilter,
    setErrorDescriptionFilter,
    setFilterBody,
    setIdFilter,
    setLocationFilter,
    setPage,
    setSortOrder,
    setStatusFilter,
  ]);

  const availableLocations = useMemo(
    () => [
      { name: 'Visos', value: '' },
      ...(locations || []).map(location => ({
        value: String(location.id),
        name: location.full_address || location.address || String(location.id),
      })),
    ],
    [locations]
  );

  useEffect(
    () => () => {
      clearDevices();
      clearFilters();
    },
    [clearDevices]
  );

  useEffect(() => {
    if (page > pageCount) {
      setPage(1);
    }
  }, [page, pageCount]);

  useEffect(() => {
    const offset = (page - 1) * PAGE_SIZE;
    fetchDevices({ ...(filterBody || {}), offset });
  }, [fetchDevices, filterBody, page]);

  const handleSubmit = useCallback(
    submitOptions => {
      const generatedFilter = [];

      if (idFilter.length) {
        generatedFilter.push({ field: 'id', value: idFilter });
      }
      if (deviceNameFilter.length) {
        generatedFilter.push({ field: 'title', value: deviceNameFilter });
      }
      if (dateTimeGtFilter) {
        generatedFilter.push({
          field: 'status_changed_at',
          expression: 'gte',
          value: dateTimeGtFilter,
          type: 'timestamp',
        });
      }
      if (dateTimeLtFilter) {
        generatedFilter.push({
          field: 'status_changed_at',
          expression: 'lte',
          value: dateTimeLtFilter,
          type: 'timestamp',
        });
      }
      if (locationFilter.length) {
        generatedFilter.push({ field: 'location_id', value: locationFilter });
      }
      if (statusFilter.length) {
        generatedFilter.push({ field: 'status', value: statusFilter });
      }
      if (errorDescriptionFilter.length) {
        generatedFilter.push({
          field: 'error_message',
          value: errorDescriptionFilter,
        });
      }

      setFilterBody(
        buildTableFilterBody({
          submitOptions,
          generatedFilter,
          sortOrder,
        })
      );
      setPage(1);
    },
    [
      idFilter,
      deviceNameFilter,
      dateTimeGtFilter,
      dateTimeLtFilter,
      locationFilter,
      statusFilter,
      errorDescriptionFilter,
      setFilterBody,
      setPage,
      sortOrder,
    ]
  );

  const tableHeader = useMemo(() => {
    if (availableLocations.length && header)
      return [
        <SelectFilter
          title="ID"
          name="id"
          valueType="number"
          array={header.id.map(id => ({ name: String(id), value: id }))}
          filterState={idFilter}
          setFilterState={setIdFilter}
          onSubmit={handleSubmit}
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
          isActive={isFilterActive('select', idFilter)}
        />,
        <SelectFilter
          title="Įrenginio pavadinimas"
          name="title"
          valueType="string"
          array={generateHeader(header.title)}
          filterState={deviceNameFilter}
          setFilterState={setDeviceNameFilter}
          onSubmit={handleSubmit}
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
          isActive={isFilterActive('select', deviceNameFilter)}
        />,
        <DateFilter
          title="Data ir laikas"
          name="status_changed_at"
          lt={dateTimeLtFilter}
          setLt={setDateTimeLtFilter}
          gt={dateTimeGtFilter}
          setGt={setDateTimeGtFilter}
          onSubmit={handleSubmit}
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
          isActive={isFilterActive('date', dateTimeLtFilter, dateTimeGtFilter)}
          titleWidth={'19rem'}
        />,
        <SelectFilter
          title="Lokacija"
          name="location_address"
          valueType="string"
          array={availableLocations}
          filterState={locationFilter}
          setFilterState={setLocationFilter}
          onSubmit={handleSubmit}
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
          isActive={isFilterActive('select', locationFilter)}
        />,
        <SelectFilter
          title="Statusas"
          name="status"
          valueType="number"
          array={generateHeader(header.status, mapDevicesStatus)}
          filterState={statusFilter}
          setFilterState={setStatusFilter}
          onSubmit={handleSubmit}
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
          isActive={isFilterActive('select', statusFilter)}
        />,
        <SelectFilter
          title="Klaidos pranešimas"
          name="error_message"
          valueType="string"
          array={generateHeader(header.last_error)}
          filterState={errorDescriptionFilter}
          setFilterState={setErrorDescriptionFilter}
          onSubmit={handleSubmit}
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
          isActive={isFilterActive('select', errorDescriptionFilter)}
        />,
      ];
    return [];
  }, [
    availableLocations,
    header,
    sortOrder,
    setSortOrder,
    idFilter,
    setIdFilter,
    deviceNameFilter,
    setDeviceNameFilter,
    locationFilter,
    setLocationFilter,
    statusFilter,
    setStatusFilter,
    errorDescriptionFilter,
    setErrorDescriptionFilter,
    handleSubmit,
  ]);

  const renderTable = () =>
    devices.map(
      ({
        id,
        title,
        status,
        status_changed_at,
        location_address,
        error_message,
      }) => (
        <TableRow url={`/monitoring/devices/${id}`} key={id}>
          <TableCell align="center">{id}</TableCell>
          <TableCell align="center">{title}</TableCell>
          <TableCell>
            {status_changed_at && formatLocaleDate(status_changed_at)}
          </TableCell>
          <TableCell>{location_address}</TableCell>
          <TableCell>
            <span style={{ color: checkBoxColor(status) }}>
              {mapDevicesStatus(status)}
            </span>
          </TableCell>
          <TableCell>{error_message}</TableCell>
        </TableRow>
      )
    );

  return (
    <WithSpinner loading={loading}>
      <Table
        header={tableHeader}
        isEmpty={!devices.length}
        filter={true}
        loading={loading}
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        serverPagination={true}
        onPageChange={setPage}
      >
        {renderTable()}
      </Table>
    </WithSpinner>
  );
};

const mapStateToProps = state => ({
  devices: state.monitoring.devices,
  locations: state.locations,
});

DevicesPage.propTypes = {
  dispatch: PropTypes.func.isRequired,
  devices: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    total: PropTypes.number.isRequired,
    devices: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        title: PropTypes.string.isRequired,
        status: PropTypes.number.isRequired,
        status_changed_at: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.oneOf([null]),
        ]),
        duration_seconds: PropTypes.oneOfType([
          PropTypes.number,
          PropTypes.oneOf([null]),
        ]),
        location_address: PropTypes.string.isRequired,
        last_error: PropTypes.string,
        error_message: PropTypes.string,
      })
    ),
    header: PropTypes.shape({
      id: PropTypes.arrayOf(PropTypes.number),
      last_error: PropTypes.arrayOf(PropTypes.string),
      location: PropTypes.arrayOf(PropTypes.string),
      location_id: PropTypes.arrayOf(PropTypes.number),
      status: PropTypes.arrayOf(PropTypes.number),
      title: PropTypes.arrayOf(PropTypes.string),
    }),
  }).isRequired,
  locations: PropTypes.shape({
    locations: PropTypes.array.isRequired,
  }).isRequired,
};

export default connect(mapStateToProps)(DevicesPage);
